<template>
  <article
    class="kb-notif"
    :class="{ 'is-unread': !notification.read }"
    :aria-labelledby="titleId"
    @click="handleClick"
  >
    <i
      :class="['pi', categoryIcon, 'kb-notif-icon']"
      aria-hidden="true"
    />
    <div class="min-w-0 grid gap-1">
      <p class="m-0 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <!-- A button so the keyboard can open it too; the whole row opens it for a pointer -->
        <button
          :id="titleId"
          type="button"
          class="kb-notif-title"
          @click.stop="handleClick"
        >
          {{ notification.title }}
        </button>
        <span
          v-if="notification.priority === 'high'"
          class="kb-tag kb-tag-alert"
        >Important</span>
        <span
          v-if="!notification.read"
          class="sr-only"
        >(unread)</span>
      </p>
      <p
        class="kb-row-hint m-0"
        :class="{ 'line-clamp-2': compact }"
      >
        {{ notification.body }}
      </p>
      <p class="m-0 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span
          class="kb-row-meta"
          :title="notification.createdAt?.toLocaleString()"
        >{{ formattedTime }}</span>
        <button
          v-if="!notification.read"
          type="button"
          class="kb-notif-action"
          :aria-label="`Mark ${notification.title} as read`"
          @click="markAsRead"
        >
          Mark as read
        </button>
        <button
          type="button"
          class="kb-notif-action is-danger"
          :aria-label="`Delete ${notification.title}`"
          @click="deleteNotification"
        >
          Delete
        </button>
      </p>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Notification, NotificationCategory } from '~/shared/types/notification'

const props = defineProps<{
  notification: Notification
  compact?: boolean
}>()

const emit = defineEmits<{
  notificationClick: [notification: Notification]
  markAsRead: [id: string]
  delete: [id: string]
}>()

// Category icon mapping
const categoryIcons = {
  system: 'pi-info-circle',
  task: 'pi-check-square',
  membership: 'pi-users',
  admin: 'pi-shield',
  message: 'pi-comment',
} as const

// Get icon for notification category
const categoryIcon = computed(() => {
  const category = props.notification.category as NotificationCategory || 'system'
  return categoryIcons[category] || 'pi-bell'
})

// One id per notification, for the row's label
const titleId = computed(() => `notification-title-${props.notification.id}`)

// Format timestamp to relative time (e.g., "2 hours ago")
const formattedTime = computed(() => {
  if (!props.notification.createdAt) return ''

  const date = props.notification.createdAt instanceof Date
    ? props.notification.createdAt
    : new Date(props.notification.createdAt)

  const now = new Date()
  const diffInMs = now.getTime() - date.getTime()
  const diffInHours = diffInMs / (1000 * 60 * 60)

  if (diffInHours < 1) {
    const minutes = Math.floor(diffInMs / (1000 * 60))
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
  }
  else if (diffInHours < 24) {
    const hours = Math.floor(diffInHours)
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`
  }
  else if (diffInHours < 168) { // 7 days
    const days = Math.floor(diffInHours / 24)
    return `${days} day${days !== 1 ? 's' : ''} ago`
  }
  else {
    // Format as date for older notifications
    return date.toLocaleDateString()
  }
})

// Handle click events
const handleClick = () => {
  emit('notificationClick', props.notification)
}

// Mark notification as read
const markAsRead = (event: Event) => {
  event.stopPropagation()
  if (!props.notification.read) {
    emit('markAsRead', props.notification.id)
  }
}

// Delete notification
const deleteNotification = (event: Event) => {
  event.stopPropagation()
  emit('delete', props.notification.id)
}
</script>

<style scoped>
/* One notification: its category icon, then title, body, time and actions.
   Unread ones carry the accent rule on the left and a bolder title. */
.kb-notif {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0 0.75rem;
  padding: 0.75rem 0.75rem 0.75rem 0.875rem;
  border-left: 2px solid transparent;
  border-bottom: 1px solid var(--kb-line);
  cursor: pointer;
}

.kb-notif:last-child {
  border-bottom: 0;
}

.kb-notif:hover {
  background: var(--kb-hover);
}

.kb-notif.is-unread {
  border-left-color: var(--kb-accent);
}

.kb-notif-icon {
  margin-top: 0.2rem;
  font-size: 0.8125rem;
  color: var(--kb-dim);
}

.kb-notif.is-unread .kb-notif-icon {
  color: var(--kb-accent);
}

.kb-notif-title {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 0.875rem;
  color: var(--kb-fg);
  text-align: left;
  cursor: pointer;
}

.kb-notif.is-unread .kb-notif-title {
  font-weight: 700;
}

.kb-notif-title:focus-visible,
.kb-notif-action:focus-visible {
  outline: 2px solid var(--kb-accent);
  outline-offset: 2px;
}

.kb-notif-action {
  padding: 0;
  border: 0;
  background: none;
  font: 12px var(--kb-font-mono);
  color: var(--kb-muted);
  cursor: pointer;
}

.kb-notif-action:hover {
  color: var(--kb-accent);
  text-decoration: underline;
}

.kb-notif-action.is-danger:hover {
  color: var(--kb-alert);
}
</style>
